import { useSyncExternalStore } from 'react';
import i18n from '../i18n';

/**
 * Cross-tab sudo tracking.
 *
 * Sudo lives in localStorage, which every tab shares, and API.fetch reads the
 * `workspace-id` header from it on every call. So the moment one tab enters or
 * exits sudo, every other tab silently starts sending the new workspace while
 * still showing the old one. This store remembers which workspace a tab
 * loaded under and flags the tab as stale once the shared value diverges.
 *
 * A stale tab is read-only (see assertSudoWritable) until the user brings the
 * shared value back to what this tab shows ("Switch to <workspace>"), or simply
 * reloads the page to adopt the shared value. Restoring makes the *other* tabs
 * stale in turn: the shared key can hold one workspace, so with N tabs N-1 are
 * always out of date. That is accepted for now.
 *
 * Entering sudo and leaving it are both full page reloads, so the snapshot is
 * always taken fresh for the workspace the page actually rendered. The user's
 * own workspace is represented by the *absence* of the keys, and it is a
 * first-class state here: a tab loaded without sudo goes stale when another
 * tab enters it, and "return" for that tab means clearing the keys again.
 */

const WORKSPACE_ID_KEY = 'workspace_id';
const WORKSPACE_NAME_KEY = 'workspace_name';
const TOKEN_KEY = 'token';
// sessionStorage: the workspace a restore asked for, re-applied on the next
// load of this tab. Two tabs restoring at once each write the shared key and
// reload, and without this the first tab to come back up reads the second
// tab's value and lands in the wrong workspace.
const RESTORE_INTENT_KEY = 'sudo_restore_intent';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** `id === null` is the user's own workspace, not "unknown". */
export type SudoWorkspace = { id: string | null; name: string | null };

let snapshot: SudoWorkspace | null = null;
let stale = false;
// Bumped when a blocked write wants the dialog back on screen.
let dialogRequests = 0;

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let sharedCache: SudoWorkspace = { id: null, name: null };

/**
 * The shared value, as every tab currently sees it.
 *
 * The two keys are written one at a time, so between the writes the pair is
 * inconsistent: after "Get out" the id is gone while the old name lingers
 * for a moment. No id means no sudo, whatever the name key says. The result
 * is cached by value so useSyncExternalStore can use it as a snapshot.
 */
export function getSharedSudo(): SudoWorkspace {
  const id = localStorage.getItem(WORKSPACE_ID_KEY);
  const name = id === null ? null : localStorage.getItem(WORKSPACE_NAME_KEY);
  if (sharedCache.id !== id || sharedCache.name !== name) {
    sharedCache = { id, name };
  }
  return sharedCache;
}

function writeSharedSudo(sudo: SudoWorkspace) {
  if (sudo.id === null) {
    localStorage.removeItem(WORKSPACE_ID_KEY);
    localStorage.removeItem(WORKSPACE_NAME_KEY);
    return;
  }
  localStorage.setItem(WORKSPACE_ID_KEY, sudo.id);
  if (sudo.name === null) {
    localStorage.removeItem(WORKSPACE_NAME_KEY);
  } else {
    localStorage.setItem(WORKSPACE_NAME_KEY, sudo.name);
  }
}

function applyRestoreIntent() {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RESTORE_INTENT_KEY);
    if (raw !== null) sessionStorage.removeItem(RESTORE_INTENT_KEY);
  } catch {
    return;
  }
  if (raw === null) return;

  try {
    const intent = JSON.parse(raw);
    if (intent && typeof intent === 'object' && 'id' in intent) {
      writeSharedSudo({
        id: typeof intent.id === 'string' ? intent.id : null,
        name: typeof intent.name === 'string' ? intent.name : null,
      });
    }
  } catch {
    // A corrupt intent is dropped; the tab simply loads under the shared value.
  }
}

/** The sudo workspace this tab loaded under, captured on first use. */
export function getLoadedSudo(): SudoWorkspace {
  if (snapshot === null) {
    // Must run before the snapshot and before the first API call. Layout's
    // render and WorkspaceChangeGuard's mount effect both get here ahead of
    // the providers' data-fetching effects.
    applyRestoreIntent();
    snapshot = getSharedSudo();
  }
  return snapshot;
}

/** Compare the shared value with what this tab loaded under. */
export function checkSudoState() {
  // A missing token means the other tab signed out, not switched workspace.
  // API.fetch bounces this tab to the login page on its next call; a "sudo
  // changed" warning on top of that would be wrong and confusing.
  if (localStorage.getItem(TOKEN_KEY) === null) return;

  const next = localStorage.getItem(WORKSPACE_ID_KEY) !== getLoadedSudo().id;
  if (next !== stale) {
    stale = next;
    emit();
  }
}

/**
 * Start watching for a switch made in another tab. Returns the cleanup.
 *
 * `storage` fires in every *other* tab the moment one of them writes, so this
 * tab learns about the switch while it sits in the background. The visibility
 * hook is the fallback for the rare browser that drops the event.
 */
export function watchSudoState(): () => void {
  getLoadedSudo();

  const onStorage = (event: StorageEvent) => {
    if (
      event.key === null ||
      event.key === WORKSPACE_ID_KEY ||
      event.key === WORKSPACE_NAME_KEY
    ) {
      checkSudoState();
      // The name arrives in its own event after the id; anything showing the
      // shared workspace must render again once it is there.
      emit();
    }
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') checkSudoState();
  };

  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

export function useSudoStale(): boolean {
  return useSyncExternalStore(subscribe, () => stale);
}

/** The shared value, re-rendering as the other tab's writes arrive. */
export function useSharedSudo(): SudoWorkspace {
  return useSyncExternalStore(subscribe, getSharedSudo);
}

/** Changes every time a blocked write asks for the dialog to reopen. */
export function useSudoDialogRequests(): number {
  return useSyncExternalStore(subscribe, () => dialogRequests);
}

function requestSudoDialog() {
  dialogRequests += 1;
  emit();
}

export class SudoStaleError extends Error {
  /** Read by showRequestError, which quotes it instead of a generic toast. */
  readonly detail: string;

  constructor() {
    const detail = i18n.t(
      'This page is from another workspace. Switch to its workspace before making changes.',
    );
    super(detail);
    this.name = 'SudoStaleError';
    this.detail = detail;
  }
}

/**
 * Refuse writes from a stale tab. Reads keep working so the page stays
 * usable, but a create or delete must not land in another tab's workspace
 * just because the user pressed "stay" on the warning.
 */
export function assertSudoWritable(method: string) {
  if (SAFE_METHODS.has(method.toUpperCase())) return;
  // The flag is event-driven; a fresh read costs nothing and covers a
  // storage event this tab has not processed yet.
  checkSudoState();
  if (!stale) return;

  requestSudoDialog();
  throw new SudoStaleError();
}

/**
 * Bring the shared value back to what this tab shows and reload in place.
 * The other tabs go stale instead; that is the trade the shared key forces.
 */
export function restoreLoadedSudo() {
  const loaded = getLoadedSudo();
  try {
    sessionStorage.setItem(RESTORE_INTENT_KEY, JSON.stringify(loaded));
  } catch {
    // Without sessionStorage the restore still works, minus the race guard.
  }
  writeSharedSudo(loaded);
  window.location.reload();
}

/** Test-only: forget the snapshot and flags between renders. */
export function resetSudoStateForTests() {
  snapshot = null;
  stale = false;
  dialogRequests = 0;
  listeners.clear();
}
